import { type DevString, Outcome, dev } from "@phosphor/utils";
import { pipeNonNull } from "@phosphor/utils";
import { atom } from "jotai";
import {
  type ActionEvent,
  type AnyAction,
  CActions,
  UKeydownRootHandler,
} from "#scripts/keyboard-demo/plugins/CActions.ts";
import { UCurrentFocus } from "#scripts/keyboard-demo/plugins/CFocusable.ts";
import { CKeydownHandler } from "#scripts/keyboard-demo/plugins/CKeydownHandler.ts";
import { CParent } from "#scripts/keyboard-demo/plugins/CParent.ts";
import {
  ENV_KEYBOARD_KIND,
  addModifiersToKeyCombo,
  normalizedKeyCombo,
} from "#scripts/keyboard-demo/utils/key-combo.ts";
import { type UID, World } from "#scripts/vmc/WorldState.ts";

// #region actions-plugin-core
export const ActionsPlugin = World.definePlugin({
  name: dev`ActionsPlugin`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/plugins/ActionsPlugin.ts:22:9" }),
  setup: (build) => {
    const { store } = build;

    // Track the currently focused entity
    const currentFocusAtom = atom((get) =>
      pipeNonNull(get(build.getUniqueAtom(UCurrentFocus)), (a) => get(a.activeFocusAtom)),
    );

    const rootUIDAtom = atom<UID | null>(null);
    const currentDispatchSpotAtom = atom((get) => get(currentFocusAtom) ?? get(rootUIDAtom));

    const handleOnce = new WeakSet<KeyboardEvent>();
    build.addUnique(UKeydownRootHandler, {
      handler(reason, keyboardEvent) {
        if (handleOnce.has(keyboardEvent)) return Outcome.Passthrough;
        handleOnce.add(keyboardEvent);
        if (keyboardEvent.defaultPrevented) return Outcome.Passthrough;
        const world = store.get(build.worldAtom);
        if (!world) return Outcome.Passthrough;
        const dispatchFromUID = store.get(currentDispatchSpotAtom);
        if (!dispatchFromUID) return Outcome.Passthrough;
        // Walk up the parent chain looking for keydown handlers
        const result = CParent.dispatch(
          dev`keydown from root`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/plugins/ActionsPlugin.ts:46:11" }).because(reason),
          world,
          dispatchFromUID,
          CKeydownHandler,
          reason,
          keyboardEvent,
        );
        // Prevent default browser behavior when we handle the key
        if (result !== Outcome.Passthrough) {
          keyboardEvent.preventDefault();
        }
        return result;
      },
      rootUIDAtom,
    });
    // #endregion actions-plugin-core

    // #region actions-plugin-binding
    // Provide keydown handler for entities with CActions
    build.onEntityCreated(
      {
        requires: [CActions],
        provides: [CKeydownHandler],
      },
      (uid, { actions }) => {
        const combinedCombosAtom = atom((get) => {
          type ComboData = {
            actionKey: string;
            handler: (reason: DevString, event: ActionEvent) => Outcome;
          } & AnyAction;

          const combosMap = new Map<string, ComboData[]>();

          for (const actionSet of actions) {
            const resolvedBindings = get(actionSet.bindingsAtom);
            for (const [actionKey, maybeHandler] of Object.entries(resolvedBindings)) {
              if (!maybeHandler) continue;
              const bindable = actionSet.registryKey.bindables[actionKey];
              if (!bindable) continue;
              const defaultKey = bindable.defaultKeybinding;
              const combo = normalizedKeyCombo(defaultKey, ENV_KEYBOARD_KIND).normalized;
              const comboData: ComboData = {
                actionKey,
                handler: maybeHandler,
                ...bindable,
              };
              const list = combosMap.get(combo);
              if (!list) {
                combosMap.set(combo, [comboData]);
              } else {
                list.push(comboData);
              }
            }
          }
          return combosMap;
        });

        const keydownHandler = CKeydownHandler.of({
          handler(reason, event) {
            // Omit shift for letter keys so "Shift+X" matches "X"
            const combos = addModifiersToKeyCombo(ENV_KEYBOARD_KIND, event, true);
            if (event.defaultPrevented) return Outcome.Passthrough;
            const combosMap = store.get(combinedCombosAtom);

            for (const combo of combos) {
              const comboDatas = combosMap.get(combo.normalized);
              if (!comboDatas) continue;
              for (const comboData of comboDatas) {
                const outcome = comboData.handler(dev`Key combo pressed: ${combo.normalized}`.ctx({ loc: "phosphor-blog/scripts/keyboard-demo/plugins/ActionsPlugin.ts:114:51" }).because(reason), {
                  target: uid,
                });
                if (outcome !== Outcome.Passthrough) {
                  return outcome;
                }
              }
            }
            return Outcome.Passthrough;
          },
        });

        return { keydownHandler };
      },
    );
    // #endregion actions-plugin-binding
  },
});
